(function () {
  "use strict";

  if (window.MarineMaxCoveoFacetSchema) return;

  const FACET_LABELS = Object.freeze({
    condition_iiq: "Condition",
    ec_in_stock: "Availability",
    make_name_iiq: "Make",
    model: "Model",
    primary_class: "Boat Type",
    length_overall: "Length",
    ec_length: "Length",
    iiq_length_overall: "Length",
    "iiq.length_overall": "Length",
    price_display: "Price Range",
    ec_price: "Price Range",
    year1: "Year",
    year_iiq: "Year",
    ec_year: "Year",
    iiq_year: "Year",
    "iiq-year": "Year",
    "iiq.year": "Year",
    model_year: "Year",
    engines: "Engine Type",
    engines_summary: "Engine Type",
    promotions: "Promotions",
  });

  const COVEO_CONDITION_ALL_LABEL = "All";
  const COVEO_CONDITION_OPTION_LABELS = Object.freeze([
    "New",
    "Used",
    "Brokerage",
  ]);

  const FRIENDLY_URL_CANONICAL_FACET_IDS = Object.freeze({
    price: "ec_price",
    length: "length_overall",
    year: "year1",
    make: "make_name_iiq",
    model: "model",
    condition: "condition_iiq",
    "boat-type": "primary_class",
    availability: "ec_in_stock",
    "engine-type": "engines",
    promotions: "promotions",
  });

  const FRIENDLY_URL_RANGE_KEYS = Object.freeze([
    "price",
    "length",
    "year",
  ]);

  const CONDITION_SORT_ORDER = Object.freeze({
    new: 0,
    used: 1,
    brokerage: 2,
  });

  const FACET_NESTED_SCALAR_KEYS = Object.freeze([
    "value",
    "caption",
    "label",
    "displayValue",
    "rawValue",
    "name",
    "text",
  ]);

  const FACET_RAW_VALUE_KEYS = Object.freeze([
    "value",
    "facetValue",
    "searchValue",
    "caption",
    "localizedCaption",
    "displayValue",
    "highlightedValue",
    "label",
    "title",
    "name",
    "key",
    "text",
    "rawValue",
    "token",
    "id",
  ]);

  const FACET_NUMERIC_IGNORED_KEYS = Object.freeze([
    "state",
    "count",
    "numberOfResults",
    "children",
    "moreValuesAvailable",
  ]);

  function normalizeFacetId(facetId) {
    return String(facetId || "").toLowerCase();
  }

  function getFacetDisplayLabel(facetId, fallback) {
    const id = normalizeFacetId(facetId).trim();
    if (FACET_LABELS[id]) return FACET_LABELS[id];
    const fallbackLabel = String(fallback || "").trim();
    if (fallbackLabel) return fallbackLabel;
    return id || "Filter";
  }

  /** Coveo Commerce facet buckets may use `value`, `facetValue`, `caption`, etc. */
  function facetNestedScalarString(value) {
    if (value == null || value === "") return "";
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value !== "object" || Array.isArray(value)) return "";

    for (const key of FACET_NESTED_SCALAR_KEYS) {
      const nestedValue = value[key];
      if (
        nestedValue != null &&
        nestedValue !== "" &&
        typeof nestedValue !== "object"
      ) {
        return String(nestedValue);
      }
    }
    return "";
  }

  function facetItemRawValue(value) {
    if (value == null) return "";
    if (typeof value === "string" || typeof value === "number") return value;
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value !== "object") return "";

    if (value.value != null && typeof value.value !== "object") {
      const primitiveValue = value.value;
      if (typeof primitiveValue === "boolean") {
        return primitiveValue ? "true" : "false";
      }
      if (primitiveValue !== "") return primitiveValue;
    }

    const nestedValue = value.value;
    if (
      nestedValue != null &&
      typeof nestedValue === "object" &&
      !Array.isArray(nestedValue)
    ) {
      if (nestedValue.value != null && nestedValue.value !== "") {
        return nestedValue.value;
      }
      if (nestedValue.min != null && nestedValue.min !== "") {
        return nestedValue.min;
      }
    }

    for (const key of FACET_RAW_VALUE_KEYS) {
      const candidate = value[key];
      if (candidate == null || candidate === "") continue;
      if (
        typeof candidate === "string" ||
        typeof candidate === "number" ||
        typeof candidate === "boolean"
      ) {
        return candidate;
      }
      const nestedScalar = facetNestedScalarString(candidate);
      if (nestedScalar) return nestedScalar;
    }

    /* Numeric buckets may expose only start/end instead of a caption/value. */
    const bounds = facetItemNumericBounds(value, {
      allowNumericValueFallback: false,
    });
    if (bounds && Number.isFinite(bounds.lo) && Number.isFinite(bounds.hi)) {
      if (Math.abs(bounds.hi - bounds.lo) < 1e-6) {
        return String(bounds.lo);
      }
      return `${String(bounds.lo)}\u2013${String(bounds.hi)}`;
    }

    const deepNumericValue = facetItemNumericValue(value, { skipRaw: true });
    return Number.isFinite(deepNumericValue) ? String(deepNumericValue) : "";
  }

  function facetItemNumericValue(value, options) {
    const skipRaw = options && options.skipRaw === true;
    if (!skipRaw) {
      const rawValue = facetItemRawValue(value);
      const numericValue = parseFloat(
        String(rawValue).replace(/[^0-9.-]/g, ""),
      );
      if (!Number.isNaN(numericValue)) return numericValue;
    }
    if (value == null || typeof value !== "object") return NaN;

    for (const key of Object.keys(value)) {
      if (FACET_NUMERIC_IGNORED_KEYS.includes(key)) continue;
      const candidate = value[key];
      if (typeof candidate === "number" && Number.isFinite(candidate)) {
        return candidate;
      }
      if (typeof candidate === "string") {
        const parsedCandidate = parseFloat(candidate.replace(/[^0-9.-]/g, ""));
        if (!Number.isNaN(parsedCandidate)) return parsedCandidate;
      }
      if (
        candidate != null &&
        typeof candidate === "object" &&
        !Array.isArray(candidate)
      ) {
        for (const nestedKey of Object.keys(candidate)) {
          const nestedCandidate = candidate[nestedKey];
          if (
            typeof nestedCandidate === "number" &&
            Number.isFinite(nestedCandidate)
          ) {
            return nestedCandidate;
          }
          if (typeof nestedCandidate === "string") {
            const parsedNestedCandidate = parseFloat(
              String(nestedCandidate).replace(/[^0-9.-]/g, ""),
            );
            if (!Number.isNaN(parsedNestedCandidate)) {
              return parsedNestedCandidate;
            }
          }
        }
      }
    }
    return NaN;
  }

  function facetItemNumericBounds(value, options) {
    const allowNumericValueFallback =
      !options || options.allowNumericValueFallback !== false;
    if (value == null || typeof value !== "object") {
      const numericValue = facetItemNumericValue(value, options);
      return Number.isFinite(numericValue)
        ? { lo: numericValue, hi: numericValue }
        : null;
    }

    const rawCandidates = [
      value.min,
      value.max,
      value.start,
      value.end,
      value.from,
      value.to,
      value.low,
      value.high,
      value.lowerBound,
      value.upperBound,
    ];

    const nestedValue = value.value;
    if (
      nestedValue &&
      typeof nestedValue === "object" &&
      !Array.isArray(nestedValue)
    ) {
      rawCandidates.push(
        nestedValue.min,
        nestedValue.max,
        nestedValue.start,
        nestedValue.end,
        nestedValue.from,
        nestedValue.to,
        nestedValue.low,
        nestedValue.high,
        nestedValue.lowerBound,
        nestedValue.upperBound,
      );
    }

    const numericCandidates = rawCandidates
      .map(function (candidate) {
        return parseFloat(String(candidate ?? "").replace(/[^0-9.-]/g, ""));
      })
      .filter(function (candidate) {
        return Number.isFinite(candidate);
      });

    if (!numericCandidates.length) {
      if (!allowNumericValueFallback) return null;
      const fallback = facetItemNumericValue(value, options);
      return Number.isFinite(fallback) ? { lo: fallback, hi: fallback } : null;
    }

    return {
      lo: Math.min(...numericCandidates),
      hi: Math.max(...numericCandidates),
    };
  }

  function facetIdsMatchCondition(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "condition_iiq" ||
      id === "condition" ||
      id === "ec_condition" ||
      id === "iiq_condition" ||
      id === "iiq.condition" ||
      id.includes("condition") ||
      id.includes("boat_status") ||
      id.includes("newused")
    );
  }

  function getCanonicalConditionOptionLabel(rawValue) {
    const normalized = String(rawValue == null ? "" : rawValue)
      .trim()
      .toLowerCase();
    if (/^new(?:\s+boat)?$/.test(normalized)) return "New";
    if (/^used(?:\s+boat)?$/.test(normalized)) return "Used";
    if (/^brokerage(?:[-\s]+boat)?$/.test(normalized)) return "Brokerage";
    return "";
  }

  function conditionValuesRepresentAll(values) {
    if (!Array.isArray(values) || !values.length) return false;
    const canonicalValues = values.map(getCanonicalConditionOptionLabel);
    if (
      canonicalValues.some(function (value) {
        return !value;
      })
    ) {
      return false;
    }
    const selectedValues = new Set(canonicalValues);
    return COVEO_CONDITION_OPTION_LABELS.every(function (label) {
      return selectedValues.has(label);
    });
  }

  /** Keep the fixed three-option Condition UI stable across partial Coveo pages. */
  function ensureCanonicalConditionFacetValues(values) {
    const liveValues = Array.isArray(values) ? values.slice() : [];
    const liveCanonicalLabels = new Set(
      liveValues
        .map(function (value) {
          return getCanonicalConditionOptionLabel(facetItemRawValue(value));
        })
        .filter(Boolean),
    );

    COVEO_CONDITION_OPTION_LABELS.forEach(function (label) {
      if (liveCanonicalLabels.has(label)) return;
      liveValues.push({
        value: label,
        state: "idle",
        __coveoSyntheticConditionOption: true,
      });
    });
    return liveValues;
  }

  function sortConditionFacetValues(values) {
    if (!Array.isArray(values) || values.length === 0) return values || [];
    return [...values].sort(function (left, right) {
      const leftValue = String(facetItemRawValue(left) || "")
        .trim()
        .toLowerCase();
      const rightValue = String(facetItemRawValue(right) || "")
        .trim()
        .toLowerCase();
      const leftCanonical =
        getCanonicalConditionOptionLabel(leftValue).toLowerCase();
      const rightCanonical =
        getCanonicalConditionOptionLabel(rightValue).toLowerCase();
      const leftOrder = Object.prototype.hasOwnProperty.call(
        CONDITION_SORT_ORDER,
        leftCanonical,
      )
        ? CONDITION_SORT_ORDER[leftCanonical]
        : Number.MAX_SAFE_INTEGER;
      const rightOrder = Object.prototype.hasOwnProperty.call(
        CONDITION_SORT_ORDER,
        rightCanonical,
      )
        ? CONDITION_SORT_ORDER[rightCanonical]
        : Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return leftValue.localeCompare(rightValue);
    });
  }

  function facetIdsMatchBoatType(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "primary_class" ||
      id === "boat_type" ||
      id === "ec_boat_type" ||
      id.includes("boat_type") ||
      id.includes("primary_class")
    );
  }

  function facetIdsMatchAvailability(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "ec_in_stock" ||
      id === "availability" ||
      id.includes("in_stock") ||
      id.includes("availability")
    );
  }

  function facetIdsMatchLength(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "length_overall" ||
      id === "ec_length" ||
      id === "iiq_length_overall" ||
      id === "iiq.length_overall" ||
      id.includes("length_overall")
    );
  }

  function facetIdsMatchPrice(facetId) {
    const id = normalizeFacetId(facetId);
    return id === "price" || id === "price_display" || id === "ec_price";
  }

  function facetIdsMatchPromotions(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "promotions" ||
      id === "promotion_name" ||
      id === "iiq_active_promotion_name" ||
      id.includes("promotion")
    );
  }

  function facetIdsMatchEngineType(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "engine_type" ||
      id === "engines" ||
      id === "engines_summary" ||
      id.includes("engine_type")
    );
  }

  function facetIdsMatchYear(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "year1" ||
      id === "year_iiq" ||
      id === "ec_year" ||
      id === "year" ||
      id === "iiq_year" ||
      id === "iiq-year" ||
      id === "iiq.year" ||
      id === "model_year" ||
      id.includes("year1") ||
      id.includes("year_iiq") ||
      id.includes("model_year") ||
      id.endsWith("_year") ||
      (id.includes("iiq") && id.includes("year"))
    );
  }

  function facetIdsMatchMake(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "make_name_iiq" ||
      id === "make_name" ||
      id === "ec_make" ||
      id === "iiq_make" ||
      id === "iiq.make" ||
      id.includes("make_name") ||
      id === "manufacturer" ||
      id === "ec_manufacturer"
    );
  }

  function facetIdsMatchModel(facetId) {
    const id = normalizeFacetId(facetId);
    return (
      id === "model" ||
      id === "ec_model" ||
      id === "model_name" ||
      id === "model_name_iiq" ||
      id.includes("model_name") ||
      (id.endsWith("_model") && !id.includes("boatmodel"))
    );
  }

  function facetUsesPagedBoatOptionsUi(facetId) {
    return facetIdsMatchMake(facetId) || facetIdsMatchModel(facetId);
  }

  function getCoveoFriendlyUrlFacetKey(facetId) {
    if (facetIdsMatchPrice(facetId)) return "price";
    if (facetIdsMatchLength(facetId)) return "length";
    if (facetIdsMatchYear(facetId)) return "year";
    if (facetIdsMatchMake(facetId)) return "make";
    if (facetIdsMatchModel(facetId)) return "model";
    if (facetIdsMatchCondition(facetId)) return "condition";
    if (facetIdsMatchBoatType(facetId)) return "boat-type";
    if (facetIdsMatchAvailability(facetId)) return "availability";
    if (facetIdsMatchEngineType(facetId)) return "engine-type";
    if (facetIdsMatchPromotions(facetId)) return "promotions";
    return "";
  }

  function getCoveoCanonicalFacetIdForFriendlyUrlKey(key) {
    const normalizedKey = String(key || "").trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(
      FRIENDLY_URL_CANONICAL_FACET_IDS,
      normalizedKey,
    )
      ? FRIENDLY_URL_CANONICAL_FACET_IDS[normalizedKey]
      : "";
  }

  function isCoveoFriendlyUrlRangeKey(key) {
    const normalizedKey = String(key || "").trim().toLowerCase();
    return FRIENDLY_URL_RANGE_KEYS.includes(normalizedKey);
  }

  function normalizeFacetTypeToken(rawValue) {
    return String(rawValue || "")
      .trim()
      .toLowerCase()
      .replace(/[-_\s]/g, "");
  }

  /** Coveo numeric range facets expose `state.type === "numericalRange"`. */
  function isNumericalRangeFacet(facet) {
    const state = facet?.state;
    if (!state || typeof state !== "object") return false;
    const facetType = normalizeFacetTypeToken(
      state.type || state.facetType || facet?.type || facet?.facetType,
    );
    return facetType === "numericalrange" || facetType === "numericrange";
  }

  function shouldUseRangeFacetUi(facet, facetId, facetLabel) {
    if (isNumericalRangeFacet(facet)) return true;
    const rangeKind = numericalRangeUiKind(facetId, facetLabel);
    return (
      rangeKind === "length" || rangeKind === "year" || rangeKind === "price"
    );
  }

  function numericalRangeUiKind(facetId, facetLabel) {
    const label = String(facetLabel || "").trim();
    if (facetIdsMatchLength(facetId) || label === "Length") return "length";
    if (facetIdsMatchYear(facetId) || label === "Year") return "year";
    if (
      facetIdsMatchPrice(facetId) ||
      label === "Price Range" ||
      label === "Price"
    ) {
      return "price";
    }
    return "numeric";
  }

  function shouldSuppressFacetValue(facetId, facetLabel, rawValue) {
    const raw = String(rawValue == null ? "" : rawValue).trim();
    if (!raw) return true;
    const normalizedValue = raw.toLowerCase();
    const isPlaceholder =
      normalizedValue === "n/a" ||
      normalizedValue === "na" ||
      normalizedValue === "none" ||
      normalizedValue === "null" ||
      normalizedValue === "undefined" ||
      normalizedValue === "-";
    if (!isPlaceholder) return false;

    const normalizedLabel = String(facetLabel || "").trim().toLowerCase();
    return (
      facetIdsMatchPromotions(facetId) ||
      facetIdsMatchEngineType(facetId) ||
      normalizedLabel === "engine type" ||
      normalizedLabel === "promotions"
    );
  }

  function normalizeFacetStateValues(state) {
    const rawValues =
      state &&
      (state.values ??
        state.facetValues ??
        state.facetValueResults ??
        null);
    if (rawValues == null) return [];
    if (Array.isArray(rawValues)) return rawValues;
    if (typeof Map !== "undefined" && rawValues instanceof Map) {
      return Array.from(rawValues.values());
    }
    if (typeof Set !== "undefined" && rawValues instanceof Set) {
      return Array.from(rawValues);
    }
    if (typeof rawValues === "object") return Object.values(rawValues);
    return [];
  }

  Object.defineProperty(window, "MarineMaxCoveoFacetSchema", {
    configurable: false,
    enumerable: false,
    value: Object.freeze({
      COVEO_CONDITION_ALL_LABEL,
      COVEO_CONDITION_OPTION_LABELS,
      FACET_LABELS,
      conditionValuesRepresentAll,
      ensureCanonicalConditionFacetValues,
      facetIdsMatchAvailability,
      facetIdsMatchBoatType,
      facetIdsMatchCondition,
      facetIdsMatchEngineType,
      facetIdsMatchLength,
      facetIdsMatchMake,
      facetIdsMatchModel,
      facetIdsMatchPrice,
      facetIdsMatchPromotions,
      facetIdsMatchYear,
      facetItemNumericBounds,
      facetItemNumericValue,
      facetItemRawValue,
      facetNestedScalarString,
      facetUsesPagedBoatOptionsUi,
      getCanonicalConditionOptionLabel,
      getCoveoCanonicalFacetIdForFriendlyUrlKey,
      getCoveoFriendlyUrlFacetKey,
      getFacetDisplayLabel,
      isCoveoFriendlyUrlRangeKey,
      isNumericalRangeFacet,
      normalizeFacetStateValues,
      normalizeFacetTypeToken,
      numericalRangeUiKind,
      shouldSuppressFacetValue,
      shouldUseRangeFacetUi,
      sortConditionFacetValues,
    }),
    writable: false,
  });
})();
